import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { classNames } from "@helpers/css";

import createRoutineMutation from "../../graphql/create-routine-mutation";
import routineSkillOptionsQuery from "../../graphql/routine-skill-options-query";
import routinesQuery from "../../graphql/routines-query";
import sendRoutineTestMutation from "../../graphql/send-routine-test-mutation";
import updateRoutineMutation from "../../graphql/update-routine-mutation";
import {
  DAY_NAMES_SHORT,
  MeetingFilter,
  RoutineTriggerType,
  formatHour,
  parseTriggerConfig,
  routineTitle,
  triggerSummary,
} from "./routine-display";
import RoutineMeetingGroupMultiselect, {
  SelectedMeetingGroup,
} from "./routine-meeting-group-multiselect";

export type RoutineEditorInitial = {
  id: string;
  name: string;
  skillId: string;
  skillSlug: string;
  triggerType: string;
  triggerConfig: string;
  selectedMeetingGroups: SelectedMeetingGroup[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initial?: RoutineEditorInitial;
};

type SkillOption = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
};

const triggerTypeOptions: ComboboxGenericOption<RoutineTriggerType>[] = [
  { value: "time_of_day", label: "At a time of day" },
  { value: "before_meeting", label: "Before a meeting" },
  { value: "after_meeting", label: "After a meeting" },
];

const hourOptions: ComboboxGenericOption<number>[] = Array.from(
  { length: 24 },
  (_, hour) => ({ value: hour, label: formatHour(hour) }),
);

const beforeLeadTimes = [5, 10, 15, 30, 60];
const afterLeadTimes = [15, 30, 60];

const RoutineEditorModal: React.FC<Props> = ({ isOpen, onClose, initial }) => {
  const label = useLabel();
  const organizationId = currentOrganizationVar().id;

  const [name, setName] = useState("");
  const [skillId, setSkillId] = useState<string | null>(null);
  const [triggerType, setTriggerType] =
    useState<RoutineTriggerType>("time_of_day");
  // time_of_day
  const [hour, setHour] = useState<number>(9);
  // Selected weekdays (0=Mon..6=Sun). Empty = every day.
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  // before/after
  const [leadTimeMinutes, setLeadTimeMinutes] = useState<number | null>(null);
  const [meetingFilter, setMeetingFilter] = useState<MeetingFilter>("all");
  const [meetingGroups, setMeetingGroups] = useState<SelectedMeetingGroup[]>(
    [],
  );

  // Reset form state whenever the modal opens (for a new or existing routine).
  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      const config = parseTriggerConfig(initial.triggerConfig) as Record<
        string,
        unknown
      >;
      setName(initial.name ?? "");
      const initialType = (initial.triggerType ||
        "time_of_day") as RoutineTriggerType;
      setTriggerType(initialType);
      setHour(typeof config.hour === "number" ? config.hour : 9);
      setDaysOfWeek(
        Array.isArray(config.days_of_week)
          ? (config.days_of_week as unknown[]).filter(
              (d): d is number => typeof d === "number",
            )
          : [],
      );
      setLeadTimeMinutes(
        typeof config.lead_time_minutes === "number"
          ? config.lead_time_minutes
          : null,
      );
      setMeetingFilter((config.meeting_filter as MeetingFilter) || "all");
      setMeetingGroups(initial.selectedMeetingGroups ?? []);
    } else {
      setName("");
      setTriggerType("time_of_day");
      setHour(9);
      setDaysOfWeek([]);
      setLeadTimeMinutes(null);
      setMeetingFilter("all");
      setMeetingGroups([]);
    }
  }, [isOpen, initial]);

  const { data: skillData } = useQuery(routineSkillOptionsQuery, {
    variables: { organizationId },
    skip: !isOpen,
  });

  // One option per skill across all three lists (no de-dupe by slug), grouped so
  // a same-slug personal and org skill both appear. The combobox value is the
  // unified skill id. Platform (Topicflow) skills are presented as Org skills.
  const skillOptions: ComboboxGenericOption<string>[] = useMemo(() => {
    if (!skillData) return [];
    const personal: SkillOption[] = skillData.mySkills ?? [];
    const workspace: SkillOption[] = skillData.workspaceSkills ?? [];
    const platform: SkillOption[] = skillData.platformSkills ?? [];
    return [
      ...personal.map((skill) => ({ skill, group: "My Skills" })),
      ...workspace.map((skill) => ({ skill, group: "Org Skills" })),
      ...platform.map((skill) => ({ skill, group: "Org Skills" })),
    ]
      .filter(({ skill }) => !!skill?.id)
      .map(({ skill, group }) => ({
        value: skill.id,
        label: skill.name,
        // Suffix only the closed-field label so an org skill stays identifiable
        // when collapsed (e.g. when a personal and an org skill share a name);
        // the dropdown rows are already grouped under My/Org Skills headers.
        selectedLabel:
          group === "Org Skills" ? `${skill.name} (org)` : undefined,
        description: skill.description || undefined,
        group,
      }));
  }, [skillData]);

  // Look up a chosen skill's slug + name from its unified id.
  const skillById = useMemo(() => {
    const map = new Map<string, { slug: string; name: string }>();
    if (!skillData) return map;
    const all: SkillOption[] = [
      ...(skillData.mySkills ?? []),
      ...(skillData.workspaceSkills ?? []),
      ...(skillData.platformSkills ?? []),
    ];
    all.forEach((skill) => {
      if (skill?.id) map.set(skill.id, { slug: skill.slug, name: skill.name });
    });
    return map;
  }, [skillData]);

  // Initialize/clear the selected skill once options are available. When editing,
  // prefer matching the stored skill id; fall back to the first matching slug for
  // legacy routines that have no skill id.
  useEffect(() => {
    if (!isOpen) return;
    if (!initial) {
      setSkillId(null);
      return;
    }
    if (initial.skillId && skillById.has(initial.skillId)) {
      setSkillId(initial.skillId);
      return;
    }
    const bySlug = skillOptions.find(
      (option) => skillById.get(option.value)?.slug === initial.skillSlug,
    );
    setSkillId(bySlug?.value ?? null);
  }, [isOpen, initial, skillOptions, skillById]);

  const meetingFilterOptions: ComboboxGenericOption<MeetingFilter>[] = useMemo(
    () => [
      { value: "all", label: "All meetings" },
      {
        value: "oneonone_only",
        label: `All ${label("oneonone", { pluralize: true })}`,
      },
      { value: "meeting_groups", label: "Selected meetings" },
    ],
    [label],
  );

  const leadTimeOptions: ComboboxGenericOption<number>[] = useMemo(() => {
    const minutes =
      triggerType === "after_meeting" ? afterLeadTimes : beforeLeadTimes;
    const direction = triggerType === "after_meeting" ? "after" : "before";
    return minutes.map((m) => ({
      value: m,
      label: `${m} minutes ${direction}`,
    }));
  }, [triggerType]);

  const [createRoutine, { loading: creating }] = useMutation(
    createRoutineMutation,
    { refetchQueries: [{ query: routinesQuery }] },
  );
  const [updateRoutine, { loading: updating }] = useMutation(
    updateRoutineMutation,
    { refetchQueries: [{ query: routinesQuery }] },
  );
  const [sendRoutineTest, { loading: testing }] = useMutation(
    sendRoutineTestMutation,
  );
  const saving = creating || updating;

  const handleSendTest = async () => {
    if (!initial?.id) return;
    try {
      await sendRoutineTest({ variables: { routineId: initial.id } });
      successNotificationVar({
        title: "Test sent",
        description:
          "This can take 30 seconds to a minute. Check your Slack or Teams.",
      });
    } catch (e) {
      errorNotificationVar({
        title: "Could not send test",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const selectedSkill = skillId ? skillById.get(skillId) : undefined;
  const skillName = selectedSkill?.name;
  const skillSlug = selectedSkill?.slug ?? "";

  const buildTriggerConfig = (): Record<string, unknown> => {
    if (triggerType === "time_of_day") {
      return { hour, days_of_week: [...daysOfWeek].sort((a, b) => a - b) };
    }
    const config: Record<string, unknown> = {
      lead_time_minutes: leadTimeMinutes,
      meeting_filter: meetingFilter,
    };
    if (meetingFilter === "meeting_groups") {
      config.meeting_group_ids = meetingGroups.map((group) => group.id);
    }
    return config;
  };

  const isValid = useMemo(() => {
    if (!skillId) return false;
    if (triggerType === "time_of_day") {
      return typeof hour === "number";
    }
    if (leadTimeMinutes === null) return false;
    if (meetingFilter === "meeting_groups" && meetingGroups.length === 0) {
      return false;
    }
    return true;
  }, [
    skillId,
    triggerType,
    hour,
    leadTimeMinutes,
    meetingFilter,
    meetingGroups,
  ]);

  const previewConfig = buildTriggerConfig();
  const previewTitle = routineTitle({ name, skillSlug }, skillName);
  const previewSummary = triggerSummary(triggerType, previewConfig, label);

  const handleSave = async () => {
    if (!isValid || !skillId || !skillSlug) return;
    const triggerConfig = JSON.stringify(buildTriggerConfig());
    const trimmedName = name.trim();
    try {
      if (initial) {
        await updateRoutine({
          variables: {
            id: initial.id,
            skillId,
            skillSlug,
            triggerType,
            triggerConfig,
            name: trimmedName,
          },
        });
      } else {
        await createRoutine({
          variables: {
            skillId,
            skillSlug,
            triggerType,
            triggerConfig,
            name: trimmedName,
            enabled: true,
          },
        });
      }
      onClose();
    } catch (e) {
      errorNotificationVar({
        title: "Could not save routine",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  if (!isOpen) return null;

  const isMeetingTrigger =
    triggerType === "before_meeting" || triggerType === "after_meeting";

  return (
    <Modal open onClose={onClose} aria-label="Routine editor dialog">
      <div className="p-6 bg-white rounded-md">
        <div className="mb-4">
          <ModalTitle onClose={onClose}>
            {initial ? "Edit routine" : "Add routine"}
          </ModalTitle>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <Input
              label="Name"
              aria-label="Routine name"
              value={name}
              placeholder={previewTitle || "Optional name"}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <div className="block text-sm font-medium text-gray-700 mb-1">
              Skill
            </div>
            <ComboboxGeneric<string>
              value={
                skillOptions.find((option) => option.value === skillId) ?? null
              }
              options={skillOptions}
              onChangeValue={(option) => setSkillId(option.value)}
              placeholder="Choose a skill..."
              aria-label="Routine skill select"
            />
          </div>

          <div>
            <div className="block text-sm font-medium text-gray-700 mb-1">
              Trigger
            </div>
            <ComboboxGeneric<RoutineTriggerType>
              value={
                triggerTypeOptions.find(
                  (option) => option.value === triggerType,
                ) ?? triggerTypeOptions[0]
              }
              options={triggerTypeOptions}
              onChangeValue={(option) => setTriggerType(option.value)}
              aria-label="Routine trigger type select"
            />
          </div>

          {triggerType === "time_of_day" && (
            <>
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-1">
                  Time of day
                </div>
                <ComboboxGeneric<number>
                  value={
                    hourOptions.find((option) => option.value === hour) ??
                    hourOptions[9]
                  }
                  options={hourOptions}
                  onChangeValue={(option) => setHour(option.value)}
                  aria-label="Routine hour select"
                />
                <div className="text-gray-500 text-xs mt-1">
                  in your timezone
                </div>
              </div>
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-1">
                  Days
                </div>
                <div className="flex flex-wrap gap-1">
                  {DAY_NAMES_SHORT.map((dayLabel, dayIndex) => {
                    const selected = daysOfWeek.includes(dayIndex);
                    return (
                      <button
                        key={dayIndex}
                        type="button"
                        aria-label={`Routine day ${dayLabel}`}
                        aria-pressed={selected}
                        onClick={() =>
                          setDaysOfWeek((current) =>
                            current.includes(dayIndex)
                              ? current.filter((d) => d !== dayIndex)
                              : [...current, dayIndex],
                          )
                        }
                        className={classNames(
                          "px-2.5 py-1 rounded text-sm border",
                          selected
                            ? "bg-blue-500 text-white border-blue-500"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
                        )}
                      >
                        {dayLabel}
                      </button>
                    );
                  })}
                </div>
                <div className="text-gray-500 text-xs mt-1">
                  Leave all unselected to run every day.
                </div>
              </div>
            </>
          )}

          {isMeetingTrigger && (
            <>
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-1">
                  Lead time
                </div>
                <ComboboxGeneric<number>
                  value={
                    leadTimeOptions.find(
                      (option) => option.value === leadTimeMinutes,
                    ) ?? null
                  }
                  options={leadTimeOptions}
                  onChangeValue={(option) => setLeadTimeMinutes(option.value)}
                  placeholder="Choose a lead time..."
                  aria-label="Routine lead time select"
                />
              </div>
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-1">
                  Which meetings
                </div>
                <ComboboxGeneric<MeetingFilter>
                  value={
                    meetingFilterOptions.find(
                      (option) => option.value === meetingFilter,
                    ) ?? meetingFilterOptions[0]
                  }
                  options={meetingFilterOptions}
                  onChangeValue={(option) => setMeetingFilter(option.value)}
                  aria-label="Routine meeting filter select"
                />
              </div>
              {meetingFilter === "meeting_groups" && (
                <RoutineMeetingGroupMultiselect
                  selected={meetingGroups}
                  onChange={setMeetingGroups}
                />
              )}
            </>
          )}

          <div
            className="text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2"
            aria-label="Routine preview"
          >
            {previewTitle || "Your routine"}
            {previewSummary ? ` — ${previewSummary}` : ""}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mt-6">
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              theme={buttonTheme.primary}
              size={ButtonSize.medium}
              onClick={handleSave}
              disabled={!isValid || saving}
              text="Save"
              aria-label="Save routine"
            />
            <Button
              theme={buttonTheme.lightGray}
              size={ButtonSize.medium}
              onClick={onClose}
              disabled={saving}
              text="Cancel"
            />
            {saving && <Loading mini size={5} />}
          </div>
          {initial?.id && (
            <Button
              theme={buttonTheme.lightGray}
              size={ButtonSize.medium}
              onClick={handleSendTest}
              disabled={saving || testing}
              text="Send test"
              aria-label="Send test routine"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RoutineEditorModal;
